import React, { useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const ToolsCarousel = ({ tools }) => {
  const scrollRef = useRef(null);
  const animationRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollPosition = 0;
    const scrollSpeed = 0.5; // pixels per frame

    const autoScroll = () => {
      if (scrollContainer) {
        scrollPosition += scrollSpeed;
        
        // Reset to beginning when reaching end (infinite loop)
        if (scrollPosition >= scrollContainer.scrollWidth / 3) {
          scrollPosition = 0;
        }
        
        scrollContainer.scrollLeft = scrollPosition;
        animationRef.current = requestAnimationFrame(autoScroll);
      }
    };

    // Start auto-scrolling
    animationRef.current = requestAnimationFrame(autoScroll);

    // Pause on hover
    const handleMouseEnter = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };

    const handleMouseLeave = () => {
      animationRef.current = requestAnimationFrame(autoScroll);
    };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (scrollContainer) {
        scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
        scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  return (
    <div className="relative group">
      {/* Left Arrow */}
      <Button
        onClick={() => scroll('left')}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-slate-800 shadow-lg hover:bg-slate-50 dark:hover:bg-slate-700 rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity"
        variant="outline"
      >
        <ChevronLeft className="h-6 w-6 text-slate-700 dark:text-slate-300" />
      </Button>

      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        className="flex gap-8 overflow-x-auto scrollbar-hide py-8 px-4"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* Duplicate tools for infinite scroll effect */}
        {[...tools, ...tools, ...tools].map((tool, index) => (
          <div
            key={index}
            className="flex-shrink-0 w-32 h-32 flex items-center justify-center bg-white dark:bg-slate-800 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-slate-200 dark:border-slate-700 group/item"
          >
            <div className="flex flex-col items-center gap-3 p-4">
              <div className="w-16 h-16 flex items-center justify-center">
                <img
                  src={tool.logo}
                  alt={tool.name}
                  className="w-full h-full object-contain group-hover/item:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div
                  className="hidden w-full h-full bg-gradient-to-br from-teal-100 to-violet-100 dark:from-teal-900/30 dark:to-violet-900/30 rounded-lg flex items-center justify-center"
                >
                  <span className="text-2xl font-bold bg-gradient-to-r from-teal-700 to-violet-700 bg-clip-text text-transparent">
                    {tool.name.charAt(0)}
                  </span>
                </div>
              </div>
              <span className="text-xs font-medium text-slate-600 dark:text-slate-400 text-center line-clamp-2">
                {tool.name}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Right Arrow */}
      <Button
        onClick={() => scroll('right')}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-slate-800 shadow-lg hover:bg-slate-50 dark:hover:bg-slate-700 rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity"
        variant="outline"
      >
        <ChevronRight className="h-6 w-6 text-slate-700 dark:text-slate-300" />
      </Button>

      {/* Gradient Overlays */}
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-slate-50 dark:from-slate-950 to-transparent pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-slate-50 dark:from-slate-950 to-transparent pointer-events-none"></div>
    </div>
  );
};

export default ToolsCarousel;
